import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  SelectChangeEvent,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  ButtonBase,
  Checkbox,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import HardwareDevicePage from '../components/hardware/HardwareDevicePage';
import {
  HW_TANK,
  HW_CONXENVIRO,
  HW_LOGGER,
  HARDWARE_TYPES,
  getTemptrackDeviceType,
  hasLoggerFields,
  hasWeatherFields,
  hasCalibrationRow,
  HDTECH_COMPANY_ID,
  isHire,
  isPolledWeatherSerial,
  LOGGER_FREQUENCIES,
  PAYMENT_TERMS,
  EMPTY_FORM,
  dateInputToIso,
  toCreatePayload,
  toRegisterPayload,
  type CompanyOption,
  type FormState,
} from '../components/hardware/hardwareFormModel';
import { typeIcon } from '../components/hardware/hardwareDisplay';
import {
  hardwareAPI,
  companiesAPI,
  type Logger,
  type LoggerUpdate,
  type LicenceType,
  type TemptrackDiscoveryItem,
  type BulkAddResponse,
} from '../services';

// Hardware Number from a TempTrack serial: the part before the "-" suffix,
// as legacy does ("AX000653-JA" -> "AX000653").
const hardwareNumberFromSerial = (serial: string): string => serial.split('-')[0];

/** Add hardware: pick a type, then one or more TempTrack discoveries (or
 *  enter a tank / weather station by hand), and assign a client. */
const HardwareAddForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [licenceTypes, setLicenceTypes] = useState<LicenceType[]>([]);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const { user } = useAuth();
  const canManage = hasPermission(user, 'hardware.edit_advanced');
  // Clients a device can be sold or hired to (HDTECH and inactive left out)
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  // TempTrack discovery picker: shown when the selected Type maps to a
  // TempTrack device_type (loggers / gateways / routers). Several can be
  // picked at once; one pick keeps the full single-device form.
  const [discoveries, setDiscoveries] = useState<TemptrackDiscoveryItem[]>([]);
  const [discoveriesLoading, setDiscoveriesLoading] = useState(false);
  const [selectedDiscoveries, setSelectedDiscoveries] = useState<TemptrackDiscoveryItem[]>([]);
  const selectedDiscovery = selectedDiscoveries.length === 1 ? selectedDiscoveries[0] : null;
  const multi = selectedDiscoveries.length > 1;
  const [bulkResult, setBulkResult] = useState<BulkAddResponse | null>(null);

  const coarseType = formData.Type;
  const temptrackDeviceType = getTemptrackDeviceType(coarseType);
  const isTemptrackEligible = temptrackDeviceType != null;

  // ConXenviro shares the TempTrack `loggers` discovery list with Logger.
  // AU-prefixed serials are ConXenviro units: only they show under
  // ConXenviro, and they are hidden from the Logger list.
  const isConxenviroSerial = (d: TemptrackDiscoveryItem): boolean =>
    (d.serial_number || '').toUpperCase().startsWith('AU');
  const visibleDiscoveries =
    coarseType === HW_CONXENVIRO
      ? discoveries.filter(isConxenviroSerial)
      : coarseType === HW_LOGGER
        ? discoveries.filter((d) => !isConxenviroSerial(d))
        : discoveries;

  useEffect(() => {
    hardwareAPI
      .getLicenceTypes()
      .then((list) => setLicenceTypes(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!canManage) return;
    let cancelled = false;
    companiesAPI
      .getCompanies()
      .then((list) => {
        if (cancelled) return;
        setCompanies(
          (Array.isArray(list) ? list : [])
            .filter((c: any) => String(c.companyid) !== HDTECH_COMPANY_ID && c.isactive !== false)
            .map((c: any) => ({ id: String(c.companyid), name: c.companyname }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canManage]);

  const handleField = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSelect = (field: keyof FormState) => (e: SelectChangeEvent) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value as string }));
  };

  // Fetch the TempTrack discoveries when the user picks a TempTrack-eligible
  // Type. The selection is cleared on Type change so a stale pick can't be
  // carried into a new device_type.
  useEffect(() => {
    setSelectedDiscoveries([]);
    setFormData((prev) => ({
      ...prev, SerialNumber: '', E2EManufactureNumber: '', HMTechNumber: '',
    }));
    if (!isTemptrackEligible || !temptrackDeviceType) {
      setDiscoveries([]);
      return;
    }
    let cancelled = false;
    setDiscoveriesLoading(true);
    hardwareAPI
      .listTemptrackDiscovered(temptrackDeviceType)
      .then((resp) => {
        if (!cancelled) setDiscoveries(resp.items || []);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setSnackbar({
            msg: e?.response?.data?.detail || 'Failed to load TempTrack discoveries',
            severity: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setDiscoveriesLoading(false);
      });
    return () => { cancelled = true; };
    // coarseType is a dep so toggling Logger <-> ConXenviro (same TempTrack
    // device_type) still clears a picked serial the other category owns.
  }, [isTemptrackEligible, temptrackDeviceType, coarseType]);

  // One pick fills Serial + Hardware Number like the legacy ddlSerialNumber;
  // several picks each get their Hardware Number from their own serial.
  const handleTemptrackSelect = (e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value;
    const ids = (typeof raw === 'string' ? raw.split(',') : raw).filter(Boolean).map(Number);
    const picked = visibleDiscoveries.filter((d) => ids.includes(d.temptrack_id));
    setSelectedDiscoveries(picked);
    const only = picked.length === 1 ? picked[0] : null;
    setFormData((prev) => ({
      ...prev,
      SerialNumber: only ? only.serial_number : '',
      E2EManufactureNumber: only ? only.serial_number : '',
      HMTechNumber: only ? hardwareNumberFromSerial(only.serial_number) : '',
    }));
  };

  const validate = (): string | null => {
    if (!formData.Type) return 'Hardware type is required';
    // Logger / Extend / Gateway must pick a discovered serial (mirrors the
    // legacy Submit() "Please select logger!" guard). Tank/Weather don't.
    if (isTemptrackEligible && selectedDiscoveries.length === 0) {
      return 'Please select a Serial Number';
    }
    if (!multi && !formData.HMTechNumber.trim()) return 'Hardware Number is required';
    return null;
  };

  const hiring = isHire(formData.SaleOrHire);

  // What create doesn't carry: the licence on TempTrack registrations, and
  // a hire company with its dates (set as a change so the hire history
  // starts like any other hire).
  const followUpFor = (fromTemptrack: boolean): LoggerUpdate => {
    const followUp: LoggerUpdate = {};
    if (fromTemptrack && formData.LicenceTypeID) followUp.LicenceTypeID = formData.LicenceTypeID;
    if (hiring && formData.CompanyID) {
      followUp.CompanyID = formData.CompanyID;
      followUp.HireStartDate = dateInputToIso(formData.HireStartDate);
      followUp.ReturnDate = dateInputToIso(formData.ReturnDate);
    }
    return followUp;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setSnackbar({ msg: err, severity: 'error' });
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (multi && temptrackDeviceType) {
        const result = await hardwareAPI.bulkAdd({
          company_id: (!hiring && formData.CompanyID) || HDTECH_COMPANY_ID,
          sale_or_hire: formData.SaleOrHire || undefined,
          items: selectedDiscoveries.map((d) => ({
            temptrack_id: d.temptrack_id,
            hm_tech_number: hardwareNumberFromSerial(d.serial_number),
            device_type: temptrackDeviceType,
          })),
        });
        const followUp = followUpFor(true);
        if (Object.keys(followUp).length > 0) {
          for (const r of result.results) {
            if (r.ok && r.logger_id) {
              // eslint-disable-next-line no-await-in-loop
              await hardwareAPI.updateLogger(r.logger_id, followUp).catch(() => {});
            }
          }
        }
        setBulkResult(result);
        return;
      }

      let created: Logger;
      if (selectedDiscovery && temptrackDeviceType) {
        // TempTrack-backed registration: backend promotes the discovery
        // on TempTrack and creates the LoggerInfo row atomically.
        created = await hardwareAPI.registerTemptrackLogger(
          toRegisterPayload(formData, selectedDiscovery, temptrackDeviceType),
        );
      } else {
        // Manual create (non-TempTrack type: tank, weather station).
        created = await hardwareAPI.createLogger(toCreatePayload(formData));
      }
      const followUp = followUpFor(!!selectedDiscovery);
      if (Object.keys(followUp).length > 0) {
        await hardwareAPI.updateLogger(created.LoggerID, followUp);
      }
      navigate(`/hardware/${created.LoggerID}`, { replace: true });
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const companyLabel = hiring
    ? t('hardware.hiredTo', 'Hired to')
    : formData.SaleOrHire
      ? t('hardware.soldTo', 'Sold to')
      : t('hardware.assignTo', 'Assign to (client)');
  const selectedCompany = companies.find((c) => c.id === formData.CompanyID) ?? null;

  const showLoggerFields = hasLoggerFields(coarseType) && !multi;
  const showWeatherFields = hasWeatherFields(coarseType);
  const showCalibration = hasCalibrationRow(coarseType) && !multi;
  const calibrationLabel = coarseType === HW_TANK
    ? t('hardware.defaultTemperature', 'Default Temperature')
    : t('hardware.calibrationFrequency', 'Calibration Frequency (Months)');

  // Form section: titled card holding a field grid.
  const section = (title: string, subtitle: string | null, children: React.ReactNode) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={2}>{children}</Grid>
      </Box>
    </Paper>
  );

  const half = { xs: 12, sm: 6 } as const;
  const dateField = (field: keyof FormState, label: string, extra?: Partial<React.ComponentProps<typeof TextField>>) => (
    <TextField
      fullWidth size="small" type="date"
      label={label}
      value={formData[field]}
      onChange={handleField(field)}
      slotProps={{ inputLabel: { shrink: true } }}
      {...extra}
    />
  );
  const textField = (field: keyof FormState, label: string, extra?: Partial<React.ComponentProps<typeof TextField>>) => (
    <TextField
      fullWidth size="small"
      label={label}
      value={formData[field]}
      onChange={handleField(field)}
      slotProps={{ inputLabel: { shrink: true } }}
      {...extra}
    />
  );
  const saleOrHireValue = hiring ? 'hire' : formData.SaleOrHire ? 'sale' : 'none';
  const saveLabel = multi
    ? t('hardware.addCount', 'Add {{count}} devices', { count: selectedDiscoveries.length })
    : t('common.create', 'Create');

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 1.5 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/hardware')} sx={{ textTransform: 'none' }}>
          {t('hardware.backToList', 'Back to Hardware')}
        </Button>
      </Box>

      {/* Title + actions */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('hardware.addTitle', 'Add Hardware')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/hardware')} disabled={saving} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? t('common.saving', 'Saving...') : saveLabel}
          </Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} alignItems="flex-start">
        {/* Left: the device itself */}
        <Grid size={{ xs: 12, md: 7, lg: 8 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {section(t('hardware.deviceSection', 'Device'), null, (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('hardware.pickType', 'What are you adding?')}
                </Typography>
                <Grid container spacing={1.5}>
                  {HARDWARE_TYPES.map((ht) => {
                    const selected = coarseType === ht.value;
                    return (
                      <Grid key={ht.value} size={{ xs: 6, sm: 4, md: 2 }}>
                        <ButtonBase
                          onClick={() => setFormData((prev) => ({ ...prev, Type: ht.value }))}
                          sx={{
                            width: '100%', flexDirection: 'column', gap: 0.75, py: 1.75, borderRadius: 2,
                            border: '1px solid', borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? 'action.selected' : 'background.paper',
                            color: selected ? 'primary.main' : 'text.secondary',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                        >
                          {typeIcon(ht.icon, 'medium')}
                          <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500, color: 'text.primary' }}>
                            {ht.label}
                          </Typography>
                        </ButtonBase>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              {/* Serial Number = the TempTrack discovery picker (legacy
                  ddlSerialNumber), now multi-select. */}
              {coarseType && isTemptrackEligible && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="serial-discovery-label">{t('hardware.serialNumbers', 'Serial Number(s)')}</InputLabel>
                    <Select<string[]>
                      multiple
                      labelId="serial-discovery-label"
                      label={t('hardware.serialNumbers', 'Serial Number(s)')}
                      value={selectedDiscoveries.map((d) => String(d.temptrack_id))}
                      onChange={handleTemptrackSelect}
                      renderValue={() => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedDiscoveries.map((d) => (
                            <Chip key={d.temptrack_id} size="small" label={d.serial_number} />
                          ))}
                        </Box>
                      )}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                    >
                      {discoveriesLoading && (
                        <MenuItem disabled><em>{t('common.loading', 'Loading…')}</em></MenuItem>
                      )}
                      {!discoveriesLoading && visibleDiscoveries.length === 0 && (
                        <MenuItem disabled><em>{t('hardware.noDiscoveries', 'No discoveries available')}</em></MenuItem>
                      )}
                      {visibleDiscoveries.map((d) => (
                        <MenuItem key={d.temptrack_id} value={String(d.temptrack_id)} disabled={d.already_registered} dense>
                          <Checkbox size="small" checked={selectedDiscoveries.some((s) => s.temptrack_id === d.temptrack_id)} />
                          <ListItemText
                            primary={d.serial_number}
                            secondary={d.already_registered ? t('hardware.alreadyRegistered', 'already registered') : undefined}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {multi && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      {t(
                        'hardware.bulkHint',
                        '{{count}} devices will be added with their Hardware Number taken from the serial, and assigned to the client on the right. Set codes, SIM and calibration on each device page afterwards.',
                        { count: selectedDiscoveries.length },
                      )}
                    </Alert>
                  )}
                </Grid>
              )}

              {coarseType && !multi && (
                <>
                  <Grid size={half}>
                    {textField('HMTechNumber', t('hardware.hardwareNumber', 'Hardware Number'), {
                      required: true,
                      // Auto-filled from the picked serial and locked, like legacy
                      // does for TempTrack-backed devices.
                      disabled: selectedDiscovery !== null,
                      helperText: selectedDiscovery ? t('hardware.fromSerial', 'Auto-filled from Serial Number') : undefined,
                    })}
                  </Grid>
                  <Grid size={half}>
                    {dateField('ManufacturedDate', t('hardware.manufacturedDate', 'Manufactured Date'))}
                  </Grid>
                  {showCalibration && (
                    <Grid size={half}>
                      {textField('CalibrationFrequencyNumber', calibrationLabel, { type: 'number' })}
                    </Grid>
                  )}
                </>
              )}
              {/* No Status field: the administrative Status defaults to '5'
                  (In Use) on create, and UP/SUSPECT/DOWN is device-reported. */}
            </>
          ))}

          {showLoggerFields && section(t('hardware.loggerSection', 'Logger Details'), null, (
            <>
              <Grid size={half}>
                <FormControl fullWidth size="small">
                  <InputLabel id="freq-label">{t('hardware.loggerFrequency', 'Logger Frequency')}</InputLabel>
                  <Select
                    labelId="freq-label"
                    label={t('hardware.loggerFrequency', 'Logger Frequency')}
                    value={formData.LoggerFrequency}
                    onChange={handleSelect('LoggerFrequency')}
                  >
                    <MenuItem value=""><em>{t('common.none', 'None')}</em></MenuItem>
                    {LOGGER_FREQUENCIES.map((f) => (
                      <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                {textField('AccessCode', t('hardware.accessCode', 'Access Code'))}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                {textField('ServiceCode', t('hardware.serviceCode', 'Service Code'))}
              </Grid>
              <Grid size={half}>
                {textField('SIMCardNumber', t('hardware.simCardNumber', 'SIM Card Number'))}
              </Grid>
              <Grid size={half}>
                {textField('SIMDataNumber', t('hardware.simDataNumber', 'SIM Data Number'))}
              </Grid>
            </>
          ))}

          {showWeatherFields && section(t('hardware.weatherSection', 'Weather Station Details'), null, (
            <>
              <Grid size={half}>{textField('DeviceName', t('hardware.deviceName', 'Device Name'))}</Grid>
              <Grid size={half}>{textField('Manufacture', t('hardware.manufacture', 'Manufacture'))}</Grid>
              <Grid size={half}>
                {textField('WSStationID', t('hardware.wsStationId', 'ZENTRA Serial'), {
                  placeholder: 'A4101946',
                  ...(formData.WSStationID.trim() && !isPolledWeatherSerial(formData.WSStationID)
                    ? {
                        color: 'warning',
                        helperText: t(
                          'hardware.wsSerialNotPolled',
                          'Only ZENTRA serials starting with "A" get weather readings.',
                        ),
                      }
                    : {}),
                })}
              </Grid>
              <Grid size={half}>
                {textField('WSApiKey', t('hardware.wsApiKey', 'ZENTRA API Token'), { type: 'password', autoComplete: 'new-password' })}
              </Grid>
              <Grid size={half}>
                {textField('WSApiSecret', t('hardware.wsApiSecret', 'API Secret'), {
                  type: 'password', autoComplete: 'new-password',
                  helperText: t('hardware.wsApiSecretHelp', 'Not used by ZENTRA stations.'),
                })}
              </Grid>
            </>
          ))}

          {!multi && section(t('common.note', 'Note'), null, (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth size="small" multiline minRows={3}
                placeholder={t('hardware.notePlaceholder', 'Anything worth knowing about this device')}
                value={formData.Note}
                onChange={handleField('Note')}
              />
            </Grid>
          ))}
        </Grid>

        {/* Right: who gets it. Sticky so it stays in view while scrolling. */}
        <Grid size={{ xs: 12, md: 5, lg: 4 }} sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          {section(t('hardware.saleHireSection', 'Sale, hire & billing'), null, (
            <>
              <Grid size={{ xs: 12 }}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  color="primary"
                  value={saleOrHireValue}
                  onChange={(_, v) => {
                    if (!v) return;
                    setFormData((prev) => ({
                      ...prev,
                      SaleOrHire: v === 'hire' ? 'For Hire' : v === 'sale' ? 'For Sale' : '',
                    }));
                  }}
                >
                  <ToggleButton value="sale" sx={{ textTransform: 'none' }}>{t('hardware.forSale', 'For Sale')}</ToggleButton>
                  <ToggleButton value="hire" sx={{ textTransform: 'none' }}>{t('hardware.forHire', 'For Hire')}</ToggleButton>
                  <ToggleButton value="none" sx={{ textTransform: 'none' }}>{t('hardware.notSet', 'Not set')}</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {/* The active client that bought or is hiring the device(s). */}
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={companies}
                  value={selectedCompany}
                  onChange={(_, v) => setFormData((prev) => ({ ...prev, CompanyID: v ? v.id : '' }))}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={companyLabel}
                      placeholder={t('hardware.notSoldOrHired', 'HDTECH (not sold or hired)')}
                      helperText={t('hardware.companyHint', 'Leave blank while it is still with HDTECH.')}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </Grid>

              {hiring && formData.CompanyID && (
                <>
                  <Grid size={half}>
                    {dateField('HireStartDate', t('hardware.hireStart', 'Hire start'), {
                      helperText: !formData.HireStartDate ? t('hardware.hireStartHint', 'Blank means today') : undefined,
                    })}
                  </Grid>
                  <Grid size={half}>
                    {dateField('ReturnDate', t('hardware.expectedReturn', 'Expected return'))}
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="licence-label">{t('hardware.licenceType', 'Licence Type')}</InputLabel>
                  <Select
                    labelId="licence-label"
                    label={t('hardware.licenceType', 'Licence Type')}
                    value={formData.LicenceTypeID}
                    onChange={handleSelect('LicenceTypeID')}
                  >
                    <MenuItem value=""><em>{t('common.none', 'None')}</em></MenuItem>
                    {licenceTypes
                      .filter((lt) => lt.IsActive !== false)
                      .map((lt) => (
                        <MenuItem key={lt.LicenceTypeID} value={lt.LicenceTypeID}>{lt.Name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {showLoggerFields && hiring && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="payterms-label">{t('hardware.paymentTerms', 'Payment Terms')}</InputLabel>
                    <Select
                      labelId="payterms-label"
                      label={t('hardware.paymentTerms', 'Payment Terms')}
                      value={formData.PaymentTerms}
                      onChange={handleSelect('PaymentTerms')}
                    >
                      <MenuItem value=""><em>{t('common.none', 'None')}</em></MenuItem>
                      {PAYMENT_TERMS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          ))}
        </Grid>
      </Grid>

      {/* Bulk add results */}
      <Dialog open={!!bulkResult} maxWidth="sm" fullWidth onClose={() => navigate('/hardware')}>
        <DialogTitle>
          {t('hardware.bulkResultTitle', '{{added}} added, {{failed}} failed', {
            added: bulkResult?.added ?? 0, failed: bulkResult?.failed ?? 0,
          })}
        </DialogTitle>
        <DialogContent>
          {bulkResult?.results.map((r) => (
            <Box key={r.temptrack_id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.75 }}>
              {r.ok
                ? <CheckCircleIcon color="success" fontSize="small" />
                : <ErrorIcon color="error" fontSize="small" />}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.hm_tech_number}</Typography>
                {r.error && <Typography variant="caption" color="error">{r.error}</Typography>}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => navigate('/hardware')}>
            {t('hardware.backToList', 'Back to Hardware')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)} variant="filled">
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/** /hardware/new adds hardware; /hardware/:id is the device's single page. */
const HardwareForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <HardwareDevicePage key={id} id={id} /> : <HardwareAddForm />;
};

export default HardwareForm;
